/**
 * `/admin/ext/<name>/settings` (HUB-103 Layer A) — an extension's DECLARED
 * settings, rendered from the hub's wire entries alone: the control comes from
 * each entry's `schema`, the help text from its `description`. One card, no
 * family grouping (extension keys carry no family prefix). Same editor hook
 * and row as the hub Configuration section, so secrets and the 503 banner
 * behave identically.
 */
import { useMemo } from 'react';
import { Alert } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { hub } from '@/lib/hub-client';
import { kindFromJsonSchema, labelFor, rowRemountKey } from '@/lib/settings-config';
import { SettingRow } from '@/pages/settings/SettingRow';
import { useSettingsEditor, type SettingsEditorApi } from '@/pages/settings/use-settings-editor';
import { extensionDisplayName } from '@/extensions/extension-nav';

type Entry = Awaited<ReturnType<typeof hub.extensionSettingsList>>['settings'][number];

export function ExtensionSettings({ name }: { name: string }) {
  const api = useMemo<SettingsEditorApi<Entry>>(
    () => ({
      list: async () => (await hub.extensionSettingsList({ params: { name } })).settings,
      put: (key, body) => hub.extensionSettingsPut({ params: { name, key }, body }),
      remove: (key) => hub.extensionSettingsDelete({ params: { name, key } }),
    }),
    [name],
  );
  const { entries, loadError, saveError, encKeyError, fieldErrors, busyKey, save, reset } =
    useSettingsEditor(api);

  return (
    <section className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold tracking-tight">{extensionDisplayName(name)} settings</h1>
        <p className="text-sm text-muted-foreground">
          Settings the <span className="font-mono">{name}</span> extension declares. Custom values take
          effect immediately; secrets are stored encrypted and never shown again.
        </p>
      </div>
      {encKeyError && <Alert variant="destructive">{encKeyError}</Alert>}
      {saveError && <Alert variant="destructive">{saveError}</Alert>}
      {loadError && <Alert variant="destructive">{loadError}</Alert>}
      {entries === null && !loadError && <div className="text-sm text-muted-foreground">Loading…</div>}
      {entries !== null && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Configuration</CardTitle>
            <CardDescription>Declared in the extension's manifest; stored by the hub.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-0 divide-y">
            {entries.map((entry) => (
              <SettingRow
                key={rowRemountKey(entry)}
                entry={entry}
                kind={kindFromJsonSchema(entry.schema)}
                label={labelFor(entry.key)}
                description={entry.description}
                error={fieldErrors[entry.key]}
                busy={busyKey === entry.key}
                onSave={(kind, raw) => save(entry, kind, raw)}
                onReset={() => void reset(entry.key)}
              />
            ))}
          </CardContent>
        </Card>
      )}
    </section>
  );
}
